<template>
  <div :class="clsx('connect-features', { 'animations-disabled': !animationEnabled })" id="connect-features">
    <IntersectionObserver
      :animation-enabled="animationEnabled"
      active-class="connect-features-intersector"
      :once="true"
      once-class="connect-features-intersector--visible"
      :threshold="0.2"
    >
      <div class="connect-features__title-block">
        <h2 class="connect-features__title" v-html="getTitleBlock('promo', 'connect-features', 'title')"></h2>
        <p class="connect-features__description" v-html="getTitleBlock('promo', 'connect-features', 'description')"></p>
      </div>

      <Laptop class="connect-features__laptop" />

      <BackgroundCircle1 class="connect-features__background-circle-1" />
      <BackgroundCircle2 class="connect-features__background-circle-2" />
      <BackgroundCircle3 class="connect-features__background-circle-3" />
      <BackgroundCircle4 class="connect-features__background-circle-4" />

      <CalendarCard class="connect-features__calendar-card" />
      <NotificationsCard class="connect-features__notifications-card" />
      <TasksCard class="connect-features__tasks-card" />
      <OrdersHistoryCard class="connect-features__orders-history-card" />
      <PartnersCard class="connect-features__partners-card" />
      <CompaniesCard class="connect-features__companies-card" />
    </IntersectionObserver>

    <div class="connect-features__bottom-gradient"></div>
  </div>
</template>

<script>
import clsx from 'clsx';
import BackgroundCircle1 from '../background-circle-1';
import BackgroundCircle2 from '../background-circle-2';
import BackgroundCircle3 from '../background-circle-3';
import BackgroundCircle4 from '../background-circle-4';
import CalendarCard from '../calendar-card';
import CompaniesCard from '../companies-card';
import Laptop from '../laptop';
import NotificationsCard from '../notifications-card';
import OrdersHistoryCard from '../orders-history-card';
import PartnersCard from '../partners-card';
import TasksCard from '../tasks-card';
import { textBlock } from '@/entities/text-block';
import { IntersectionObserver } from '@/shared/ui/intersection-observer';

export default {
  props: {
    animationEnabled: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [ textBlock ],
  components: {
    Laptop, BackgroundCircle1, BackgroundCircle2, BackgroundCircle3, BackgroundCircle4, CalendarCard, NotificationsCard,
    TasksCard, OrdersHistoryCard, PartnersCard, CompaniesCard, IntersectionObserver,
  },
  methods: { clsx },
};
</script>

<style lang="scss" scoped>
.connect-features {
  position: relative;
  height: 680px;
  overflow: hidden;

  @media (max-width: 600px) {
    height: 550px;
  }

  &__title-block {
    position: absolute;
    top: 80px;
    left: 0;
    right: 0;
    z-index: 1;

    @media (max-width: 600px) {
      top: 40px;
    }
  }

  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 52px;
    font-weight: 500;
    color: #313437;
    text-align: center;
    margin-bottom: 16px;
    transform: translateY(100%);
    opacity: 0;
    animation: slideup-text-animation 1s linear forwards;
    animation-delay: 2.5s;
    animation-play-state: paused;

    @media (max-width: 600px) {
      font-size: 46px;
    }
  }

  &__description {
    font-size: 18px;
    font-weight: 400;
    color: #494E53;
    text-align: center;
    transform: translateY(100%);
    opacity: 0;
    animation: slideup-text-animation 1s linear forwards;
    animation-delay: 2.5s;
    animation-play-state: paused;
  }

  &__laptop {
    position: absolute;
    bottom: 20px;
    left: 0;
    right: 0;
    opacity: 0;
    transform: translateY(105%) scale(0.5);
    z-index: 4;
    animation: laptop-animation 1s linear forwards;
    animation-play-state: paused;

    @media (max-width: 600px) {
      bottom: -50px;
    }
  }

  &__background-circle-1,
  &__background-circle-2,
  &__background-circle-3,
  &__background-circle-4 {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    transform: translateY(100%);
    animation: slide-up-animation 2.5s linear forwards;
    animation-delay: 1.5s;
    animation-play-state: paused;

    @media (max-width: 1500px) {
      left: -45%;
      right: -45%;
    }

    @media (max-width: 1300px) {
      left: -90%;
      right: -90%;
    }

    @media (max-width: 600px) {
      left: -35%;
      right: -35%;
    }

    @media (max-width: 550px) {
      left: -45%;
      right: -45%;
    }

    @media (max-width: 500px) {
      left: -55%;
      right: -55%;
    }

    @media (max-width: 450px) {
      left: -65%;
      right: -65%;
    }

    @media (max-width: 400px) {
      left: -80%;
      right: -80%;
    }
  }

  &__calendar-card {
    position: absolute;
    bottom: 0;
    left: calc(50% + 380px);
    transform: translateY(100%);
    animation: slide-up-animation 0.5s linear forwards;
    animation-delay: 1s;
    animation-play-state: paused;
    z-index: 3;

    @media (max-width: 1000px) {
      right: -20px;
    }

    @media (max-width: 900px) {
      right: -70px;
    }

    @media (max-width: 800px) {
      right: -100px;
    }

    @media (max-width: 700px) {
      right: -115px;
    }

    @media (max-width: 600px) {
      right: -160px;
    }
  }

  &__notifications-card {
    position: absolute;
    bottom: 0;
    left: calc(50% + 280px);
    transform: translateY(100%);
    animation: slide-up-animation 0.5s linear forwards;
    animation-delay: 1.5s;
    animation-play-state: paused;
    z-index: 2;

    @media (max-width: 1000px) {
      right: -10px;
    }

    @media (max-width: 900px) {
      right: -60px;
    }

    @media (max-width: 800px) {
      right: -95px;
    }

    @media (max-width: 700px) {
      right: -130px;
    }

    @media (max-width: 600px) {
      right: -150px;
    }
  }

  &__tasks-card {
    position: absolute;
    bottom: 0;
    left: calc(50% + 140px);
    transform: translateY(100%);
    animation: slide-up-animation 0.5s linear forwards;
    animation-delay: 2s;
    animation-play-state: paused;
    z-index: 1;

    @media (max-width: 1000px) {
      right: -5px;
    }

    @media (max-width: 900px) {
      right: -65px;
    }

    @media (max-width: 800px) {
      right: -100px;
    }

    @media (max-width: 700px) {
      right: -120px;
    }

    @media (max-width: 600px) {
      right: -165px;
    }
  }

  &__orders-history-card {
    position: absolute;
    bottom: 0;
    left: calc(50% - 670px);
    transform: translateY(100%);
    animation: slide-up-animation 0.5s linear forwards;
    animation-delay: 1s;
    animation-play-state: paused;
    z-index: 3;
  }

  &__partners-card {
    position: absolute;
    bottom: 0;
    left: calc(50% - 580px);
    transform: translateY(100%);
    animation: slide-up-animation 0.5s linear forwards;
    animation-delay: 1.5s;
    animation-play-state: paused;
    z-index: 2;
  }

  &__companies-card {
    position: absolute;
    bottom: 55px;
    left: calc(50% - 450px);
    transform: translateY(100%);
    animation: slide-up-animation 0.5s linear forwards;
    animation-delay: 2s;
    animation-play-state: paused;
    z-index: 1;
  }

  &__bottom-gradient {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(180deg, rgba(252, 253, 255, 0) 0%, #FCFDFF 70%, #FCFDFF 100%);
    height: 170px;
    z-index: 3;
  }
}

.connect-features-intersector {
  &--visible {
    .connect-features__laptop,
    .connect-features__background-circle-1,
    .connect-features__background-circle-2,
    .connect-features__background-circle-3,
    .connect-features__background-circle-4,
    .connect-features__calendar-card,
    .connect-features__notifications-card,
    .connect-features__orders-history-card,
    .connect-features__partners-card,
    .connect-features__tasks-card,
    .connect-features__companies-card,
    .connect-features__title,
    .connect-features__description {
      animation-play-state: running;
    }
  }
}

.animations-disabled,
.animations-disabled * {
  animation: none !important;
  animation-duration: 0s !important;
  animation-delay: 0s !important;
  transition: none !important;
}

.animations-disabled {
  .connect-features {
    &__title, &__description, &__background-circle-1, &__background-circle-2, &__background-circle-3,
    &__background-circle-4, &__calendar-card, &__notifications-card, &__tasks-card, &__orders-history-card,
    &__partners-card, &__companies-card {
      transform: translateY(0);
      opacity: 1;
    }

    &__laptop {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }
}

@keyframes laptop-animation {
  from {
    opacity: 0;
    transform: translateY(105%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes slide-up-animation {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes slideup-text-animation {
  0% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 0%, 0 0%);
    transform: translateY(100%);
  }
  20% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 20%, 0 20%);
    transform: translateY(80%);
  }
  40% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 40%, 0 40%);
    transform: translateY(60%);
  }
  60% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 60%, 0 60%);
    transform: translateY(40%);
  }
  80% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 80%, 0 80%);
    transform: translateY(20%);
  }
  100% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    transform: translateY(0);
  }
}

</style>
